// English source strings of wave 5 (decision D23): the awaiting-human column and decision card, production
// accounting, the council, planning agents and parallelism, the agents leaderboard and playbooks, contacts and
// notification channels, and the magic-link / MFA sign-in states. Merged into `en`; pt-BR lives in pt-BR-wave5.ts
// (complete, checked by the type). Plain language: the people deciding are not always engineers.
export const enWave5 = {
  // Awaiting human decision (Kanban)
  "approval.columnHint": "The council decides whatever it can. These tasks need your answer to continue.",
  "approval.loadFailed": "Could not load the pending questions",
  "approval.waitingFor": "Waiting {duration}",
  "approval.options": "Answers",
  "approval.recommended": "recommended",
  "approval.replyCode": "Reply code",
  "approval.replyHint": "To answer by WhatsApp or Telegram, send “{example}”.",
  "approval.glossary": "What the terms mean",
  "approval.note": "Note for the team",
  "approval.missing": "The question for this task is not available yet.",
  "approval.decided": "Answer recorded for task #{number}: {option}.",
  "approval.runNotStarted": "The answer was recorded, but the work did not restart: {reason}",
  "approval.alreadyDecided": "Task #{number} was already answered by someone else. The board was updated.",
  "approval.needsDecision": "Needs your decision",
  "approval.yourDecision": "Your decision",
  "approval.haltedNotice.paused": "The project is paused. This option puts the task back into production; resume the project first.",
  "approval.haltedNotice.draining": "The project is draining. This option puts the task back into production; resume the project first.",
  "approval.haltedNotice.stopped": "The project is stopped. This option puts the task back into production; resume the project first.",
  "approval.resumeProject": "Resume project",
  "approval.resumeSuccess": "Project resumed.",

  // Production accounting
  "production.title": "Production",
  "production.projectTitle": "Production",
  "production.loadFailed": "Could not load the production figures",
  "production.productionTime": "Time producing",
  "production.waitingHuman": "Waiting for a person",
  "production.blockedTime": "Blocked",
  "production.attempts": "Attempts",
  "production.attemptsValue": "{ok} succeeded · {failed} failed",
  "production.tokens": "Tokens (input · output)",
  "production.tokensValue": "{input} · {output}",
  "production.cost": "Known cost",
  "production.unknownCost": "cost unknown in {n} calls",
  "production.models": "Models used",
  "production.agents": "Agents",
  "production.noModels": "No model calls recorded yet.",
  "production.noAgents": "No agent attempts recorded yet.",
  "production.tasksDone": "Tasks done",
  "production.doneOf": "{done} of {total}",
  "production.tasksWorking": "In production now",
  "production.tasksAwaitingHuman": "Waiting for a person",
  "production.tasksBlocked": "Blocked",
  "production.elapsed": "Elapsed since the first start",
  "production.col.model": "Model",
  "production.col.agent": "Agent",
  "production.col.calls": "Calls",
  "production.col.attempts": "Attempts",
  "production.col.errorRate": "Errors",
  "production.col.successRate": "Success",
  "production.col.tokens": "Tokens (in · out)",
  "production.col.cost": "Known cost",
  "production.col.shareDone": "% of done tasks",
  "production.col.shareWork": "% of work time",

  // Council
  "council.title": "Council",
  "council.loadFailed": "Could not load the council reviews",
  "council.none": "The council has not reviewed this task.",
  "council.applied": "What happened: {action}",
  "council.singleCall": "Five perspectives from one model call, not independent agents.",
  "council.verdict.approve_once": "Approved this time",
  "council.verdict.approve_rule": "Approved as a rule",
  "council.verdict.deny": "Denied",
  "council.verdict.clarify": "Needs clarification",
  "council.verdict.escalate_human": "Sent to a person",
  "council.action.unblocked_for_retry": "unblocked to try again",
  "council.action.paid_escalation_approved": "approved a stronger (paid) attempt",
  "council.action.escalated_to_human": "asked a person to decide",
  "council.action.kept_blocked": "kept blocked",

  // Project overview
  "insights.title": "Production and decisions",
  "insights.refresh": "Update figures",
  "decisions.title": "Human decisions",
  "decisions.pending": "Waiting for an answer",
  "decisions.decided": "Answered",
  "decisions.timeToDecide": "Time to answer (median · 90%)",
  "decisions.timeToResume": "Time until work resumed (median · 90%)",
  "decisions.medianP90": "{median} · {p90}",
  "decisions.council": "{escalated} of {reviews} council reviews needed a person.",
  "decisions.byChannel": "Answered by:",
  "decisions.hint": "90% means nine out of ten answers took at most that long.",
  "channel.web": "Panel",
  "channel.whatsapp": "WhatsApp",
  "channel.telegram": "Telegram",
  "channel.email": "E-mail",
  "channel.api": "API",
  "planning.title": "Planning",
  "planning.start": "Plan with agents",
  "planning.hint": "Agents read the latest PRD, split it into tasks, pick the agent for each one and order them so no task starts before what it depends on.",
  "planning.requested": "Planning requested. The tasks appear on the board when it finishes.",
  "planning.none": "No planning yet.",
  "planning.tasksCreated": "{n} tasks created.",
  "planning.state.requested": "Waiting to start",
  "planning.state.running": "Planning",
  "planning.state.completed": "Done",
  "planning.state.failed": "Failed",
  "planning.mode.agent": "Planned by an agent",
  "planning.mode.rules_fallback": "Planned by the basic rules (agent unavailable)",
  "parallelism.title": "Work in parallel",
  "parallelism.summary": "Recommended: {recommended} tasks at a time (up to {max} could run together; project limit {limit}).",
  "parallelism.facts": "{waves} rounds · longest chain of dependent tasks: {critical} · waiting on dependencies: {blocked}",
  "parallelism.none": "No open tasks to schedule.",
  "parallelism.wave": "Round {n}:",

  // Agents page
  "agents.tab.leaderboard": "Performance",
  "agents.tab.playbooks": "Playbooks",
  "agents.subtitle.leaderboard": "Which agents and models deliver, and which ones need attention.",
  "agents.subtitle.playbooks": "The instructions each platform agent follows. Read-only.",
  "leaderboard.hint": "Finished attempts in the period. An item is flagged when it has enough attempts and fails more than it succeeds.",
  "leaderboard.emptyTitle": "No finished attempts in this period",
  "leaderboard.emptyBody": "The ranking appears once agents finish work.",
  "leaderboard.agents": "Agents",
  "leaderboard.models": "Models",
  "leaderboard.col.status": "Assessment",
  "leaderboard.underperforming": "Below expectations",
  "leaderboard.suggestAgent": "Improve this agent or change its model.",
  "leaderboard.suggestModel": "Consider changing this model.",
  "leaderboard.ok": "OK",
  "playbooks.hint": "Playbooks are maintained with the platform; they are shown here so everyone knows how each agent works.",
  "playbooks.emptyTitle": "No playbooks available",
  "playbooks.instructions": "Show instructions",
  "playbooks.version": "version {version}",
  "playbooks.evaluation": "How the result is checked",

  // Settings: contacts and notifications
  "settings.tab.notifications": "Contacts & notifications",
  "contact.title": "My contacts",
  "contact.intro": "Where we ask you for decisions. WhatsApp or Telegram come first; otherwise a daily e-mail lists what is waiting, with a link that opens the board without a password.",
  "contact.channels": "How to reach me",
  "contact.receive.whatsapp": "Receive questions on WhatsApp",
  "contact.receive.telegram": "Receive questions on Telegram",
  "contact.linked.whatsapp": "Linked to {id}",
  "contact.linked.telegram": "Linked to chat {id}",
  "contact.notLinked": "{channel} is not linked yet.",
  "contact.unlink": "Unlink {channel}",
  "contact.unlinked": "{channel} unlinked.",
  "contact.linkGenerate": "Generate link code",
  "contact.linkCode": "Your code:",
  "contact.linkHow.whatsapp": "Send {code} from your phone to the platform's WhatsApp number within {n} minutes. The number that sends it is linked to your account.",
  "contact.linkHow.telegram": "Send {code} to the platform's Telegram bot within {n} minutes. The chat that sends it is linked to your account.",
  "contact.linkSent": "I sent it — check again",
  "contact.emailDigest": "Daily e-mail with the decisions waiting for me",
  "contact.emailDigestHint": "Sent once a day, only when something is waiting.",
  "contact.preferences": "Preferences",
  "contact.digestHour": "Daily e-mail time",
  "contact.timezone": "Time zone",
  "contact.timezoneHint": "For example America/Sao_Paulo.",
  "contact.timezoneInvalid": "Use a time zone like America/Sao_Paulo.",
  "contact.locale": "Language of the messages",
  "contact.saved": "Contacts saved.",
  "channels.title": "Notification channels",
  "channels.intro": "Organization-wide connections used to send questions and receive answers. Passwords and tokens are never shown again after saving.",
  "channels.state": "Status",
  "channels.saved": "{channel} saved.",
  "channels.configured": "Configured",
  "channels.notConfigured": "Not configured",
  "channels.about.whatsapp": "WhatsApp through your GOWA server.",
  "channels.about.telegram": "Telegram through a bot created with BotFather.",
  "channels.about.email": "E-mail through your SMTP server (daily summary).",
  "channels.secret.whatsapp": "GOWA password",
  "channels.secret.telegram": "Bot token",
  "channels.secret.email": "SMTP password",
  "channels.secretHint": "Leave blank to keep the current one.",
  "channels.webhookSecret": "Webhook secret",
  "channels.webhookSecretLength": "Use at least {n} characters.",
  "channels.inboundUrl": "Address for incoming messages",
  "channels.inboundPending": "Save the channel with its credentials to get the address.",
  "channels.inboundHint.whatsapp": "Paste it as the webhook URL in GOWA, with the same webhook secret.",
  "channels.inboundHint.telegram": "Register it as the bot webhook (setWebhook) with the same secret token.",
  "channels.copy": "Copy",
  "channels.copied": "Address copied.",
  "channelField.base_url": "GOWA address",
  "channelField.username": "User",
  "channelField.api_url": "Telegram API address",
  "channelField.host": "SMTP server",
  "channelField.port": "Port",
  "channelField.from": "Sender (From)",
  "channelField.security": "Connection security",
  "channelField.security.tls": "TLS",
  "channelField.security.starttls": "STARTTLS",
  "channelField.security.none": "None (not recommended)",
  "notifications.title": "My notifications",
  "notifications.emptyTitle": "No notifications yet",
  "notifications.emptyBody": "Questions and summaries sent to you appear here.",
  "notifications.col.when": "When",
  "notifications.col.channel": "Channel",
  "notifications.col.subject": "Subject",
  "notifications.col.state": "Status",
  "notifications.kind.approval_request": "Decision request",
  "notifications.kind.daily_digest": "Daily summary",
  "notifications.kind.support_reply": "Support reply",
  "notifications.state.queued": "Queued",
  "notifications.state.sent": "Sent",
  "notifications.state.failed": "Failed",
  "notifications.state.skipped": "Not sent",

  // Sign-in
  "auth.magicExpiredTitle": "This link has expired or was already used",
  "auth.magicExpiredBody": "No problem: sign in normally below. Links from the daily e-mail work once and for a limited time.",
  "mfa.verifyCodeSubtitle": "Enter the 6-digit code from your authenticator app to continue.",
} as const;
